// pages/SAPMasterDataCard.jsx — "Master data & settings" card in the Process Map.
// Sub-tabs (material, vendor, customer, other master data, release strategy, output
// types, movement types, storage locations); each tab starts with a plain-language
// "what is this" box for readers without functional SAP background.
// Scope switch: landscape summary (sapMasterData.js) or per organisation — plant,
// purchasing org, sales org, company code (SAPOrgSection / sapOrgMasterData.js).
// When a company chip is selected in the Process Map, the per-org view highlights/filters it.
// Static snapshot 29-10-2026. Bilingual ID/EN.
import { useState, useEffect } from 'react';
import { TABS, WHAT, MD } from './sapMasterData';
import SAPOrgSection from './SAPOrgSection';

const T = {
  en: { title: 'Master data & settings', sub: 'Material, vendor, customer, release strategy, output, movement types, storage locations — per landscape or per plant / purchasing org / sales org / company', what: 'What is this?', findings: 'What stands out', show: 'Show', hide: 'Hide', scopeLand: 'Landscape summary', scopeOrg: 'Per organisation' },
  id: { title: 'Master data & setting', sub: 'Material, vendor, customer, release strategy, output, movement type, storage location — per landscape atau per plant / purchasing org / sales org / company', what: 'Ini apa?', findings: 'Yang menonjol', show: 'Buka', hide: 'Tutup', scopeLand: 'Ringkasan landscape', scopeOrg: 'Per organisasi' },
};

function Bars({ title, rows, fmt, color }) {
  const max = Math.max(...rows.map(r => r[1]), 1);
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</div>
      {rows.map(([label, n]) => (
        <div key={label} className="grid items-center gap-2 text-[12.5px]" style={{ gridTemplateColumns: 'minmax(0,1fr) 72px' }}>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="truncate text-slate-700" title={label}>{label}</span>
            <div className="h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full" style={{ width: `${(n / max) * 100}%`, background: color }} /></div>
          </div>
          <span className="font-mono text-right tabular-nums text-slate-700">{fmt(n)}</span>
        </div>
      ))}
    </div>
  );
}

export default function SAPMasterDataCard({ landKey, lang, coCode, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState('material');
  const [scope, setScope] = useState('land'); // 'land' | 'org'
  const [onlyCo, setOnlyCo] = useState(true);
  const L = lang === 'id' ? 'id' : 'en';
  const t = T[L];

  // selecting a company chip switches to the per-org view for that company
  useEffect(() => { if (coCode) { setScope('org'); setOnlyCo(true); } }, [coCode]);

  const data = MD[landKey];
  if (!data) return null;
  const d = data[tab];
  const fmt = n => n.toLocaleString(L === 'id' ? 'id-ID' : 'en-US');

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-col gap-3">
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-3 text-left w-full">
        <span className="text-[14px] font-bold text-slate-800">{t.title}</span>
        <span className="text-[11.5px] text-slate-400 flex-1 min-w-0 truncate">{t.sub}</span>
        <span className="text-[12px] font-semibold text-brand-700 shrink-0">{open ? `▲ ${t.hide}` : `▼ ${t.show}`}</span>
      </button>
      {open && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
              {[['land', t.scopeLand], ['org', t.scopeOrg]].map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => setScope(k)}
                  className={`px-3 py-1 rounded-md text-[12.5px] font-semibold ${scope === k ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <div className="w-px h-5 bg-slate-200 mx-1" />
            <div className="flex flex-wrap gap-1.5" role="tablist">
              {TABS.map(tb => (
                <button key={tb.key} type="button" role="tab" aria-selected={tab === tb.key} onClick={() => setTab(tb.key)}
                  className={`px-3 py-1 rounded-full text-[12.5px] font-semibold border ${tab === tb.key ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-700 border-slate-200 hover:border-brand-300'}`}>
                  {tb[L]}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-sky-50 border border-sky-100 px-3.5 py-2.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-sky-700 mb-0.5">{t.what}</div>
            <p className="text-[12.5px] text-slate-700 m-0 leading-relaxed">{WHAT[tab][L]}</p>
          </div>

          {scope === 'org' && (
            <SAPOrgSection landKey={landKey} tab={tab} lang={L} coCode={coCode} onlyCo={onlyCo} setOnlyCo={setOnlyCo} />
          )}

          {scope === 'land' && d && (
            <>
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                {d.kpi.map(([en, id, n]) => (
                  <div key={en} className="border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <div className="font-mono text-[16px] font-bold text-slate-800 tabular-nums">{fmt(n)}</div>
                    <div className="text-[11px] text-slate-500 leading-tight">{L === 'id' ? id : en}</div>
                  </div>
                ))}
              </div>

              {d.bars.length > 0 && (
                <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  {d.bars.map((b, i) => <Bars key={i} title={b.title[L]} rows={b.rows} fmt={fmt} color={i % 2 ? '#b07812' : '#11766f'} />)}
                </div>
              )}

              {d.notes.length > 0 && (
                <div className="rounded-lg bg-brand-50 border border-brand-100 px-3.5 py-2.5">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-brand-700 mb-1">{t.findings}</div>
                  <ul className="list-disc pl-4 text-[12.5px] text-slate-700 space-y-1">{d.notes.map((n, i) => <li key={i}>{n[L]}</li>)}</ul>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
